import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Sparkles, Plus, Layers, DollarSign, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { starApi } from "@/lib/extensions-api";
import { formatCurrency, formatNumber, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { StarOfferFormModal } from "./StarOfferFormModal";
import type { StarOffer } from "@/types/star";

export function StarOffersPage() {
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<StarOffer | null>(null);
  const qc = useQueryClient();
  const nav = useNavigate();
  
  const { data, isLoading } = useQuery({
    queryKey: ["admin-star-offers"],
    queryFn: starApi.admin.offers,
  });

  const deleteOffer = useMutation({
    mutationFn: (id: number) => starApi.admin.updateOffer(id, { status: "disabled" }),
    onSuccess: () => {
      toast.success("Offer has been disabled (deleted).");
      qc.invalidateQueries({ queryKey: ["admin-star-offers"] });
      setDeleteTarget(null);
    },
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Star Offers</h1>
          <p className="text-sm text-surface-500 mt-1">
            Create Fixed Bonuses or Global Pool (Royalty) targets for Star Members.
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setOpen(true)}>
          New Offer
        </Button>
      </header>

      <Card>
        <CardHeader><CardTitle>All Offers</CardTitle></CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (data ?? []).length === 0 ? (
            <EmptyState icon={Sparkles} title="No offers yet"
                          description="Create your first Star Offer above." />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Period</TH>
                  <TH className="text-right">Targets</TH>
                  <TH>Strategy</TH>
                  <TH className="text-right">Reward</TH>
                  <TH>Status</TH>
                  <TH className="text-right">Actions</TH>
                </TR>
              </THead>
              <tbody>
                {data!.map((o) => (
                  <TR key={o.id} onClick={() => nav(`/admin/star/offers/${o.id}`)} className="cursor-pointer hover:bg-surface-50">
                    <TD>
                      <div className="font-semibold text-surface-900">{o.name}</div>
                      <div className="text-2xs text-surface-500 mt-0.5">{formatDateTime(o.created_at)}</div>
                    </TD>
                    <TD><StatusPill status={o.period_type} /></TD>
                    <TD className="text-right font-mono text-xs">
                      {formatNumber(o.own_point_target)} / {formatNumber(o.combined_point_target)}
                    </TD>
                    <TD>
                      {o.reward_strategy === "pool" ? (
                        <div className="inline-flex items-center gap-1.5 bg-brand-50 text-brand-700 border border-brand-200 px-2 py-0.5 rounded-md text-xs font-semibold">
                          <Layers className="h-3 w-3" /> Pool
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1.5 bg-success-50 text-success-700 border border-success-200 px-2 py-0.5 rounded-md text-xs font-semibold">
                          <DollarSign className="h-3 w-3" /> Fixed
                        </div>
                      )}
                    </TD>
                    <TD className="text-right">
                      {o.reward_strategy === "pool" ? (
                        <div className="font-semibold text-brand-700">+{formatCurrency(o.contribution_per_entry)} <span className="text-xs font-normal">/entry</span></div>
                      ) : (
                        <div className="font-semibold text-success-700 text-base">{formatCurrency(o.bonus_amount)}</div>
                      )}
                    </TD>
                    <TD><StatusPill status={o.status} /></TD>
                    <TD className="text-right">
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          size="sm" variant="ghost" 
                          onClick={() => nav(`/admin/star/offers/${o.id}`)}
                        >
                          <Edit className="h-4 w-4 text-surface-500 hover:text-brand-600" />
                        </Button>
                        <Button 
                          size="sm" variant="ghost"
                          disabled={o.status === "disabled"}
                          onClick={() => setDeleteTarget(o)}
                        >
                          <Trash2 className="h-4 w-4 text-surface-500 hover:text-danger-600" />
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <StarOfferFormModal open={open} onClose={() => setOpen(false)} />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Offer?"
        message={`Are you sure you want to delete the offer "${deleteTarget?.name}"? It will be permanently disabled and users will no longer earn from it.`}
        confirmLabel="Yes, Disable it"
        destructive
        loading={deleteOffer.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteOffer.mutate(deleteTarget.id);
        }}
      />
    </div>
  );
}